'use client'

import { HomepageQueryResult, PageQueryResult } from "@/sanity.types";
import MuxPlayer from "@mux/mux-player-react";
import Image from "next/image";
import NextLink from "next/link";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";

import JoinWaitlist from "./JoinWaitlist";
import { Link } from "./Link";
import Logo from "./Logo";
import StampLayeredLife from "./StampLayeredLife";

import useMediaQuery from "@/hooks/useMediaQuery";
import ColorScheme from "./colorScheme";

type HeroProps = Extract<NonNullable<NonNullable<HomepageQueryResult>["content"]>[number], { _type: "hero" }> | Extract<NonNullable<NonNullable<PageQueryResult>["content"]>[number], { _type: "hero" }>;

export default function Hero(props: HeroProps) {
  const MAX_WIDTH = 1023; // Tailwind's 'lg' breakpoint
  const pathname = usePathname();
  const {copy, cta, hasArrowDown, hasOverlay, hasWaitlistForm, image, mobileImage, mobileVideo, title, video } = props;
  const [media, setMedia] = useState(video || image);
  const [isFormVisible, setIsFormVisible] = useState<boolean>(false);
  const playerRef = useRef<any>(null);

  const isMobile = useMediaQuery(`(max-width: ${MAX_WIDTH}px)`);

  const handlePlayerRef = useCallback((el: any) => {
    playerRef.current = el;
    if (!el) return;
    const tryPlay = () => {
      const mediaEl = el.media?.nativeEl ?? el.querySelector?.('video') ?? el;
      if (mediaEl && typeof mediaEl.play === 'function') {
        mediaEl.play().catch(() => {});
      }
    };
    el.addEventListener('canplay', tryPlay, { once: true });
    el.addEventListener('loadeddata', tryPlay, { once: true });
  }, []);

  useEffect(() => {
    const nextMedia = isMobile
      ? (mobileVideo || video || mobileImage || image)
      : (video || image);
    setMedia(nextMedia);
  }, [isMobile, image, mobileImage, mobileVideo, video]);

  return (
    <section className="bg-blueberry first:-mt-(--header-height) flex w-full min-w-0 flex-col overflow-hidden relative text-lilac min-h-svh">
      {media && (
        <figure className="absolute inset-0 z-0">
          {media._type === 'imageAlt'
            ? (
              media.assetPath && (
                <Image
                  src={media.assetPath}
                  alt={media.caption ?? ''}
                  fill={true}
                  loading="eager"
                  priority={true}
                  className="object-center object-cover"
                />
              )
            ) : (
              <MuxPlayer
                ref={handlePlayerRef}
                key={media.playbackId ?? ''}
                autoPlay
                loop
                muted
                playsInline
                playbackId={media.playbackId ?? ''}
                accentColor="var(--color-lilac)"
                poster={`https://image.mux.com/${media.playbackId}/thumbnail.jpg?time=${media.asset?.thumbTime}`}
                preload="auto"
                className="absolute inset-0 object-center object-cover"
                style={{
                  '--controls': 'none',
                  '--media-object-fit': 'cover',
                  '--media-object-position': 'center',
                }}
              />
            )
        }
          {hasOverlay && <div className="absolute inset-0 bg-linear-to-b from-transparent to-black opacity-30 z-10 pointer-events-none" />}
        </figure>
      )}
      <div className={`flex min-w-0 justify-between ${pathname === "/" ? "bg-transparent margin relative text-lilac" : "h-13.5 bg-(--color-background) border-b border-(--color-foreground) px-5 md:px-7.5 py-2.5 sticky top-0"}`}>
        <NextLink href="/" className={pathname === "/" ? "logo-wrapper min-w-0 shrink" : "logo-wrapper flex min-w-0 shrink justify-start py-1.75"}>
          <Logo modifier={pathname === "/" ? 'w-full h-full' : 'h-full'} />
        </NextLink>

        {pathname === "/" && <div className="stamp-wrapper w-[161px] max-md:hidden self-center">
          <StampLayeredLife />
        </div>}
      </div>

      <div className={`md:flex items-end justify-between margin mt-auto relative ${hasWaitlistForm ? 'max-md:pb-[calc(env(safe-area-inset-bottom)+1.5625rem)]!' : 'max-md:pb-6.25!'}`}>
        <div className="flex flex-col gap-7.5 md:w-3/5">
          {title && <h1 className="text-box-trim-both">{title}</h1>}
          {copy && <p className="md:w-3/5">{copy}</p>}
          {hasArrowDown && <svg
            width="23"
            height="30"
            viewBox="0 0 23 30"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="max-md:hidden animate-pulse"
          >
            <path d="M11.1897 1V27.7772M11.1897 27.7772L1 19.4075M11.1897 27.7772L21.3259 19.4075" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>}
        </div>

        {hasWaitlistForm
          ? (
            <JoinWaitlist
              colorScheme="grape"
              isLargeText={true}
              embedded={true}
              modifier="rounded md:w-96 max-md:mt-6.25 w-full sticky bottom-[calc(var(--spacing) * 7.5)]"
            />
          )
          : cta && (
            <ColorScheme colorScheme="grape">
              <Link
                link={cta}
                className="button text-[var(--color-foreground)] bg-[var(--color-background)]  large cursor-pointer flex items-center justify-center max-md:mt-6.25 px-12.5 py-3 rounded  text-center transition-colors w-full md:w-112.5 leading-1 "
              >
                {cta.text || 'Learn More'}
              </Link>
            </ColorScheme>
          )
        }
      </div>
    </section>
  );
}
